import {
  EditorFeatures,
  TiptapEditorCore,
  TiptapEditorHandle,
  useTiptapFileHandler,
} from '@src/ui/Editor';
import { PortalEditorConfig } from '@src/ui/Editor/constants/editorConfig';
import { Editor } from '@tiptap/react';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';

import { TenantToolbar, TenantToolbarEventHandlers } from './TenantToolbar';
import { TenantTiptapEditorProps } from './types';

/**
 * TenantTiptapEditor
 * Specialized editor for tenant (Portal) context with specific features enabled
 */
export const TenantTiptapEditor = forwardRef<TiptapEditorHandle, TenantTiptapEditorProps>(
  (
    {
      value,
      onChange,
      enabled,
      height,
      width,
      containerHeight,
      editorRef,
      isInitFocus = false,
      uploadFunction,
      onDragEnter,
      onDragLeave,
      onDrop,
      ...rest
    },
    ref,
  ) => {
    const internalEditorRef = useRef<{ getEditor: () => Editor | null }>(null);

    const fileHandler = useTiptapFileHandler({
      editor: internalEditorRef.current?.getEditor() || null,
      editorConfig: PortalEditorConfig,
      useAlerts: true, // Tenant uses alerts for error messages
      enableImageInsertion: true, // Tenant inserts images directly to editor
      attachments: [],
      onAttachmentChange: () => {},
      customUploadFunction: uploadFunction,
    });

    // Ref to hold eventHandlers from TiptapEditorCore
    const coreEventHandlers = useRef<TenantToolbarEventHandlers>({});

    // Callback to receive eventHandlers from TiptapEditorCore
    const handleEventHandlersReady = (eventHandlers: TenantToolbarEventHandlers) => {
      coreEventHandlers.current = eventHandlers;
    };

    useImperativeHandle(ref, () => ({
      getEditor: () => internalEditorRef.current?.getEditor() || null,
      resetErrorMessage: () => {
        fileHandler.resetErrorMessage();
      },
      selectFiles: (files: FileList) => {
        fileHandler.handleSelectFile(files);
      },
      setAttachmentError: (error: string) => {
        fileHandler.setAttachmentError(error);
      },
      blur: () => {
        const editor = internalEditorRef.current?.getEditor();
        editor?.commands.blur();
      },
    }));

    const defaultTenantFeatures: EditorFeatures = useMemo(
      () => ({
        richText: true,
        mentions: true,
        images: true,
        links: true,
        tables: true,
        iframes: true,
        textStyles: true,
        lists: true,
        codeBlocks: true,
        quotes: true,
        dividers: true,
        history: true,
        dragDrop: true,
        fileUpload: !!uploadFunction,
        fileSave: false,
        shortcut: {
          underline: true,
          strike: false,
          heading: true,
          textAlign: true,
          blockquote: false,
        },
      }),
      [uploadFunction],
    );

    const finalFeatures = useMemo(
      () => (enabled ? { ...defaultTenantFeatures, ...enabled } : defaultTenantFeatures),
      [enabled, defaultTenantFeatures],
    );

    return (
      <TiptapEditorCore
        ref={internalEditorRef}
        features={finalFeatures}
        value={value}
        onChange={onChange}
        height={height}
        width={width}
        className='!px-24 !py-4 xl:!px-8 lg:!px-4'
        containerHeight={containerHeight}
        editorRef={editorRef}
        isInitFocus={isInitFocus}
        enableMentions={false}
        placeholderText=''
        uploadFunction={uploadFunction}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onEventHandlersReady={handleEventHandlersReady}
        {...rest}
      >
        <TenantToolbar
          editor={internalEditorRef.current?.getEditor() || null}
          features={finalFeatures}
          eventHandlers={{
            onSelectFile: fileHandler.handleSelectFile,
            handleOpenLinkAddModal: coreEventHandlers.current.handleOpenLinkAddModal,
            openConvertUrlModal: coreEventHandlers.current.openConvertUrlModal,
            openIFrameCodeModal: coreEventHandlers.current.openIFrameCodeModal,
            openInsertTableModal: coreEventHandlers.current.openInsertTableModal,
          }}
        />
      </TiptapEditorCore>
    );
  },
);

TenantTiptapEditor.displayName = 'TenantTiptapEditor';
